import { inject, Injectable } from '@angular/core'
import { ApiClient, ApiClientFactory, serializeHttpParams } from '@ftr/api-shared'
import {
  CreateOrganizationRequest,
  InviteOrganizationUsersRequest,
  ListOrganizationUsersQuery,
  ListOrganizationUsersResponse,
  Organization,
  OrganizationInvite,
} from '@ftr/contracts/api/organizations'
import { Uuid } from '@ftr/contracts/type/shared'
import { ApiResult } from '@ftr/foundation'

@Injectable({ providedIn: 'root' })
export class OrganizationsService {
  private readonly userApiClient: ApiClient = inject(ApiClientFactory).build('/user')
  private readonly organizationsApiClient: ApiClient = inject(ApiClientFactory).build('/organizations')

  /**
   * Creates an organization for the signed in user, who becomes its first admin. The region it
   * lives in is derived from `countryCode` by the API.
   */
  create(body: CreateOrganizationRequest): ApiResult<Organization> {
    return this.organizationsApiClient.post<Organization>({ body })
  }

  fetchForCurrentUser(): ApiResult<Organization[]> {
    return this.userApiClient.get<Organization[]>({ path: '/organizations' })
  }

  listUsers(organizationId: Uuid, query: ListOrganizationUsersQuery): ApiResult<ListOrganizationUsersResponse> {
    return this.organizationsApiClient.get<ListOrganizationUsersResponse>({
      path: `/${organizationId}/users`,
      params: serializeHttpParams(query),
    })
  }

  removeUser(organizationId: Uuid, userId: Uuid): ApiResult<undefined> {
    return this.organizationsApiClient.delete<undefined>({ path: `/${organizationId}/users/${userId}` })
  }

  acceptInvite(organizationId: Uuid): ApiResult<undefined> {
    return this.organizationsApiClient.put<undefined>({ path: `/${organizationId}/accept-invite` })
  }

  declineInvite(organizationId: Uuid): ApiResult<undefined> {
    return this.organizationsApiClient.delete<undefined>({ path: `/${organizationId}/decline-invite` })
  }

  inviteUsers(body: InviteOrganizationUsersRequest): ApiResult<OrganizationInvite[]> {
    return this.organizationsApiClient.post<OrganizationInvite[]>({
      path: '/invite',
      body,
    })
  }

  resendInvite(organizationId: Uuid, userId: Uuid): ApiResult<undefined> {
    return this.organizationsApiClient.put<undefined>({
      path: `/${organizationId}/users/${userId}/resend-invite`,
    })
  }
}
